import { authApiService } from './authService';

const MAX_SUBSCRIPTION_DAYS = {
  monthly: 31,
  annually: 366,
  default: 400,
};

function maxSubscriptionDays(billingPeriod) {
  if (billingPeriod === 'monthly') return MAX_SUBSCRIPTION_DAYS.monthly;
  if (billingPeriod === 'annually') return MAX_SUBSCRIPTION_DAYS.annually;
  return MAX_SUBSCRIPTION_DAYS.default;
}

function computeDaysUntil(endDate, billingPeriod = null) {
  if (!endDate) return null;
  const end = new Date(endDate);
  if (Number.isNaN(end.getTime())) return null;
  const diffMs = end.getTime() - Date.now();
  if (diffMs <= 0) return 0;
  const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  const maxDays = maxSubscriptionDays(billingPeriod);
  return days > maxDays ? null : days;
}

function inferSubscriptionEndFromStart(subscriptionStart, billingPeriod) {
  if (!subscriptionStart || !billingPeriod) return null;
  const start = new Date(subscriptionStart);
  if (Number.isNaN(start.getTime())) return null;
  const end = new Date(start);
  end.setMonth(end.getMonth() + (billingPeriod === 'annually' ? 12 : 1));
  return end.toISOString();
}

export function sanitizeSubscriptionDisplay({
  subscriptionEnd,
  subscriptionStart = null,
  billingPeriod = null,
} = {}) {
  let end = subscriptionEnd || null;
  let days = computeDaysUntil(end, billingPeriod);

  if (days == null && end && subscriptionStart && billingPeriod) {
    const inferredEnd = inferSubscriptionEndFromStart(subscriptionStart, billingPeriod);
    const inferredDays = computeDaysUntil(inferredEnd, billingPeriod);
    if (inferredDays != null) {
      end = inferredEnd;
      days = inferredDays;
    }
  }

  return {
    subscriptionEnd: end,
    subscriptionDaysRemaining: days,
  };
}

function formatBillingPeriodLabel(period) {
  if (!period) return null;
  if (period === 'annually') return 'Annual';
  if (period === 'monthly') return 'Monthly';
  return String(period).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Premium display label — includes recurring payment wording. */
export function formatRecurringBillingLabel(period) {
  const base = formatBillingPeriodLabel(period);
  if (!base) return 'Recurring subscription';
  return `${base} · Recurring payment`;
}

// Simple cache for API responses
const apiCache = {
  platformAccess: { data: null, timestamp: 0, ttl: 30000 }, // 30 seconds
  trialAvailability: { data: null, timestamp: 0, ttl: 60000 }, // 60 seconds
};

const getCachedData = (key) => {
  const cache = apiCache[key];
  if (cache && cache.data && (Date.now() - cache.timestamp) < cache.ttl) {
    return cache.data;
  }
  return null;
};

const setCachedData = (key, data) => {
  apiCache[key] = {
    data,
    timestamp: Date.now(),
    ttl: apiCache[key]?.ttl || 30000
  };
};

const clearCache = (key = null) => {
  if (key) {
    apiCache[key] = { data: null, timestamp: 0, ttl: 30000 };
  } else {
    Object.keys(apiCache).forEach(k => {
      apiCache[k] = { data: null, timestamp: 0, ttl: 30000 };
    });
  }
};

export const subscriptionService = {
  clearSubscriptionCache: () => clearCache(),

  // ===============================
  // SUBSCRIPTION STATUS ROUTES
  // ===============================

  // Get detailed subscription status
  getSubscriptionStatus: async () => {
    return authApiService.get('/subscriptions/status');
  },

  // Check platform access (simple boolean)
  checkPlatformAccess: async (forceRefresh = false) => {
    try {
      // Check cache first
      if (!forceRefresh) {
        const cachedData = getCachedData('platformAccess');
        if (cachedData) {
          return cachedData;
        }
      }

      const response = await authApiService.get('/subscriptions/access');
      
      // Handle backend response structure: { statusCode: 200, body: { data: {...} } }
      let data;
      if (response.data) {
        if (response.data.statusCode && response.data.body) {
          data = response.data.body.data;
        } else if (response.data.data) {
          data = response.data.data;
        } else {
          data = response.data;
        }
      } else {
        data = response;
      }
      
      const result = { 
        data, 
        hasAccess: data?.hasAccess || false,
        success: true 
      };

      // Cache the result
      setCachedData('platformAccess', result);
      
      return result;
    } catch (error) {
      return { 
        hasAccess: false, 
        error: error.message,
        success: false 
      };
    }
  },

  // Check if user is subscribed (simple API for frontend)
  isUserSubscribed: async () => {
    return authApiService.get('/subscriptions/is-subscribed');
  },

  // Get payment history
  getPaymentHistory: async () => {
    return authApiService.get('/subscriptions/payments');
  },

  // ===============================
  // SUBSCRIPTION ACTIONS
  // ===============================

  // Use a trial (for free tier)
  useTrial: async () => {
    try {
      const response = await authApiService.post('/subscriptions/use-trial');
      
      // Handle backend response structure: { statusCode: 200, body: { data: {...} } }
      let data;
      if (response.data) {
        if (response.data.statusCode && response.data.body) {
          data = response.data.body.data;
        } else if (response.data.data) {
          data = response.data.data;
        } else {
          data = response.data;
        }
      } else {
        data = response;
      }
      
      const result = { 
        data, 
        success: true,
        remainingTrials: data?.remainingTrials || 0,
        trialUsageCount: data?.trialUsageCount || 0
      };

      // Clear cache after trial usage to ensure fresh data
      clearCache('trialAvailability');
      clearCache('platformAccess');

      return result;
    } catch (error) {
      return { 
        success: false, 
        error: error.message,
        data: null
      };
    }
  },

  // Upgrade to premium after payment
  upgradeToPremium: async (paymentId) => {
    const response = await authApiService.post('/subscriptions/upgrade', { paymentId });
    clearCache();
    return response;
  },

  // ===============================
  // PROTECTED FEATURE EXAMPLES
  // ===============================

  // Free tier feature (consumes trial)
  accessFreeFeature: async () => {
    return authApiService.get('/subscriptions/free-feature');
  },

  // Premium only feature
  accessPremiumFeature: async () => {
    return authApiService.get('/subscriptions/premium-feature');
  },

  // Feature that requires valid payment (for premium users)
  accessPaymentRequiredFeature: async () => {
    return authApiService.get('/subscriptions/payment-required-feature');
  },

  // ===============================
  // BUSINESS LOGIC ROUTES
  // ===============================

  // Main platform access endpoint (use this for your main app features)
  requestPlatformAccess: async () => {
    return authApiService.post('/subscriptions/platform-access');
  },

  // ===============================
  // UTILITY METHODS
  // ===============================

  // Check trial availability
  checkTrialAvailability: async (forceRefresh = false) => {
    try {
      if (forceRefresh) {
        clearCache('trialAvailability');
      }

      // Check cache first
      if (!forceRefresh) {
        const cachedData = getCachedData('trialAvailability');
        if (cachedData) {
          return cachedData;
        }
      }

      const response = await authApiService.get('/subscriptions/status');
      
      // Handle backend response structure: { statusCode: 200, body: { data: {...} } }
      let data;
      if (response.data) {
        if (response.data.statusCode && response.data.body) {
          data = response.data.body.data;
        } else if (response.data.data) {
          data = response.data.data;
        } else {
          data = response.data;
        }
      } else {
        data = response;
      }
      
      let result;
      if (data && (data.planType || data.plan_type || data.accountLimits)) {
        const limits = data.accountLimits;
        const planType = data.planType || data.plan_type || 'free';
        const isPremium = limits?.isPremium === true;
        const canCreateChart =
          limits?.canCreateChart ?? data.canCreateChart ?? true;
        const canUsePlatform =
          limits?.canUsePlatform ?? data.canUsePlatform ?? true;
        const hasAccess = data.hasAccess ?? canUsePlatform;
        const accessReason =
          limits?.restrictionReason ||
          data.accessReason ||
          (isPremium
            ? 'PREMIUM_ACCESS'
            : canUsePlatform && canCreateChart
              ? 'FREE_TRIAL_VALID'
              : 'UNKNOWN');

        if (!isPremium) {
          result = {
            hasTrialsLeft: canUsePlatform && canCreateChart,
            remainingTrials: data.remainingTrials || 0,
            totalTrials: data.maxFreeTrials || data.max_free_trials || 0,
            usedTrials: data.trialUsageCount || data.trial_usage_count || 0,
            planType: 'free',
            hasAccess,
            accessReason,
            canCreateChart,
            canUsePlatform,
            trialStarted:
              data.trialStarted ?? limits?.trialPeriod?.hasStarted ?? false,
            trialStartedAt:
              data.trialStartedAt ?? limits?.trialPeriod?.startedAt ?? null,
            trialEndsAt: data.trialEndsAt ?? limits?.trialPeriod?.endsAt ?? null,
            daysRemaining:
              data.daysRemaining ?? limits?.trialPeriod?.daysRemaining ?? null,
            chartsUsed:
              data.chartsUsed ?? limits?.chartLimit?.chartsUsed ?? 0,
            chartsRemaining:
              data.chartsRemaining ?? limits?.chartLimit?.chartsRemaining ?? null,
            maxCharts:
              data.maxCharts ??
              limits?.chartLimit?.maxChartsPerDay ??
              data.limitConfig?.maxChartsPerDay ??
              25,
            maxChartsPerDay:
              data.maxChartsPerDay ??
              limits?.chartLimit?.maxChartsPerDay ??
              data.limitConfig?.maxChartsPerDay ??
              25,
            maxChartsOverTrial:
              data.maxChartsOverTrial ??
              limits?.chartLimit?.maxChartsOverTrial ??
              data.limitConfig?.maxChartsOverTrial ??
              175,
            trialDays:
              data.trialDays ??
              limits?.trialPeriod?.trialDays ??
              data.limitConfig?.trialDays ??
              7,
            limitConfig: data.limitConfig ?? limits?.limitConfig ?? null,
            accountLimits: limits || null,
          };
        } else {
          const paymentInfo = data.paymentInfo ?? data.latestPayment ?? null;
          const subscriptionEnd =
            data.subscriptionEnd ??
            data.subscription_end ??
            paymentInfo?.coverageEnd ??
            null;
          const billingPeriod =
            paymentInfo?.billingPeriod ??
            paymentInfo?.billing_period ??
            data.billingPeriod ??
            data.billing_period ??
            null;
          const planKey =
            data.planKey ??
            data.plan_key ??
            paymentInfo?.planKey ??
            paymentInfo?.plan_key ??
            limits?.planKey ??
            'professional';
          const seatCount =
            paymentInfo?.seatCount ??
            paymentInfo?.seat_count ??
            data.seatCount ??
            data.seat_count ??
            1;

          const subscriptionStart =
            data.subscriptionStart ?? data.subscription_start ?? null;
          const sanitized = sanitizeSubscriptionDisplay({
            subscriptionEnd,
            subscriptionStart,
            billingPeriod,
          });

          const isStarter = String(planKey).toLowerCase() === 'starter';
          // Soft Professional trial: Pro features without payment; daily chart cap still applies.
          const softTrial = Boolean(data.isTrial ?? limits?.isTrial);
          const dailyRemaining =
            data.chartsRemaining ?? limits?.chartLimit?.chartsRemaining;
          const monthlyRemaining =
            data.chartsRemaining ?? limits?.chartLimit?.chartsRemaining;
          const monthlyMax =
            data.maxChartsPerMonth ??
            limits?.chartLimit?.maxChartsPerMonth ??
            data.maxCharts ??
            limits?.chartLimit?.maxCharts ??
            50;

          const trialDaysRemaining =
            data.daysRemaining ?? limits?.trialPeriod?.daysRemaining ?? null;

          result = {
            hasTrialsLeft: softTrial
              ? canUsePlatform && canCreateChart
              : true,
            remainingTrials: -1,
            totalTrials: -1,
            usedTrials: 0,
            // Keep planType as free during soft trial; entitlements come from accountLimits.isPremium.
            planType: softTrial ? 'free' : 'premium',
            isTrial: softTrial,
            hasAccess,
            accessReason: softTrial
              ? limits?.restrictionReason ||
                data.accessReason ||
                'FREE_TRIAL_VALID'
              : accessReason,
            canCreateChart,
            canUsePlatform,
            trialStarted:
              data.trialStarted ?? limits?.trialPeriod?.hasStarted ?? softTrial,
            trialStartedAt:
              data.trialStartedAt ?? limits?.trialPeriod?.startedAt ?? null,
            trialEndsAt: data.trialEndsAt ?? limits?.trialPeriod?.endsAt ?? null,
            daysRemaining: softTrial ? trialDaysRemaining : -1,
            subscriptionEnd: softTrial ? null : sanitized.subscriptionEnd,
            subscriptionStart: softTrial ? null : subscriptionStart,
            billingPeriod: softTrial ? null : billingPeriod,
            billingPeriodLabel: softTrial
              ? null
              : formatRecurringBillingLabel(billingPeriod),
            planKey,
            seatCount,
            noteHistoryDays:
              data.noteHistoryDays ?? limits?.noteHistoryDays ?? (isStarter ? 30 : null),
            subscriptionDaysRemaining: softTrial
              ? null
              : sanitized.subscriptionDaysRemaining,
            chartsUsed: data.chartsUsed ?? limits?.chartLimit?.chartsUsed ?? 0,
            chartsRemaining: softTrial
              ? dailyRemaining ?? 0
              : isStarter
                ? monthlyRemaining ?? 0
                : -1,
            maxCharts: softTrial
              ? data.maxChartsPerDay ??
                limits?.chartLimit?.maxChartsPerDay ??
                data.limitConfig?.maxChartsPerDay ??
                25
              : isStarter
                ? monthlyMax
                : data.maxCharts ?? 25,
            maxChartsPerDay:
              data.maxChartsPerDay ??
              limits?.chartLimit?.maxChartsPerDay ??
              data.limitConfig?.maxChartsPerDay ??
              25,
            maxChartsPerMonth: isStarter ? monthlyMax : null,
            maxChartsOverTrial:
              data.maxChartsOverTrial ??
              limits?.chartLimit?.maxChartsOverTrial ??
              data.limitConfig?.maxChartsOverTrial ??
              175,
            trialDays:
              data.trialDays ??
              limits?.trialPeriod?.trialDays ??
              data.limitConfig?.trialDays ??
              7,
            emrInputEligible:
              data.emrInputEligible ?? limits?.emrInputEligible ?? false,
            emrInputPriceCents:
              data.emrInputPriceCents ?? limits?.emrInputPriceCents ?? null,
            earlyAccess:
              data.earlyAccess ?? limits?.earlyAccess ?? false,
            priorityFeatureRequests:
              data.priorityFeatureRequests ??
              limits?.priorityFeatureRequests ??
              false,
            emrTurnaround:
              data.emrTurnaround ?? limits?.emrTurnaround ?? null,
            limitConfig: data.limitConfig ?? limits?.limitConfig ?? null,
            accountLimits: limits || null,
          };
        }
      } else {
        result = {
          hasTrialsLeft: false,
          remainingTrials: 0,
          totalTrials: 0,
          usedTrials: 0,
          planType: 'free',
          hasAccess: false,
          accessReason: 'NO_DATA'
        };
      }

      // Cache the result
      setCachedData('trialAvailability', result);
      
      return result;
    } catch (error) {
      return {
        hasTrialsLeft: false,
        remainingTrials: 0,
        totalTrials: 0,
        usedTrials: 0,
        planType: 'free',
        hasAccess: false,
        accessReason: 'ERROR'
      };
    }
  },

  getEmrSettings: async () => {
    const response = await authApiService.get('/subscriptions/emr-input');
    const payload = response.data;
    if (payload?.statusCode && payload?.body) return payload.body.data;
    return payload?.data ?? payload ?? response;
  },

  setEmrEnabled: async (enabled) => {
    const response = await authApiService.put('/subscriptions/emr-input', {
      enabled: Boolean(enabled),
    });
    const payload = response.data;
    if (payload?.statusCode && payload?.body) return payload.body.data;
    return payload?.data ?? payload ?? response;
  },

  listEmrCharges: async (params = {}) => {
    const response = await authApiService.get('/subscriptions/emr-input/charges', {
      params,
    });
    const payload = response.data;
    if (payload?.statusCode && payload?.body) return payload.body.data;
    return payload?.data ?? payload ?? response;
  },

  requestSessionEmrInput: async (sessionId, { notes } = {}) => {
    const response = await authApiService.post(
      `/sessions/${sessionId}/emr-input`,
      { notes: notes || null }
    );
    const payload = response.data;
    if (payload?.statusCode && payload?.body) return payload.body.data;
    return payload?.data ?? payload ?? response;
  },

  // Handle trial usage with automatic payment redirect
  handleTrialUsage: async (onTrialUsed, onPaymentRequired) => {
    try {
      // First check if user has access
      const accessResponse = await subscriptionService.checkPlatformAccess();
      const accessData = accessResponse.data || accessResponse;
      
      if (!accessData.success) {
        throw new Error(accessData.message || 'Access check failed');
      }

      const accessInfo = accessData.data;
      
      if (!accessInfo.hasAccess) {
        // User needs to upgrade or make payment
        if (onPaymentRequired) {
          onPaymentRequired(accessInfo);
        }
        return {
          success: false,
          requiresPayment: true,
          reason: accessInfo.reason,
          needsUpgrade: accessInfo.needsUpgrade,
          needsPayment: accessInfo.needsPayment
        };
      }

      // User has access, consume trial if free tier
      if (accessInfo.subscription?.planType === 'free') {
        const trialResponse = await subscriptionService.useTrial();
        const trialData = trialResponse.data || trialResponse;
        
        if (trialData.success && onTrialUsed) {
          onTrialUsed(trialData.data);
        }
        
        return {
          success: true,
          trialUsed: true,
          remainingTrials: trialData.data?.remainingTrials || 0,
          planType: 'free'
        };
      }

      // Premium user - no trial tracking needed
      return {
        success: true,
        trialUsed: false,
        planType: 'premium'
      };

    } catch (error) {
      throw error;
    }
  },

  // Simplified access check for components
  canAccessFeature: async () => {
    try {
      const response = await subscriptionService.isUserSubscribed();
      const data = response.data || response;
      
      if (data.success && data.data) {
        return {
          canAccess: data.data.isSubscribed,
          planType: data.data.planType || 'free',
          hasPaymentIssue: data.data.hasPaymentIssue,
          reason: data.data.reason
        };
      }
      
      return {
        canAccess: false,
        planType: 'free',
        hasPaymentIssue: false,
        reason: 'UNKNOWN'
      };
    } catch (error) {
      return {
        canAccess: false,
        planType: 'free',
        hasPaymentIssue: false,
        reason: 'ERROR'
      };
    }
  },

  // Get subscription summary for dashboard
  getSubscriptionSummary: async () => {
    try {
      const response = await subscriptionService.getSubscriptionStatus();
      const data = response.data || response;
      
      if (data.success && data.data) {
        const subscription = data.data;
        
        return {
          planType: subscription.planType || 'free',
          status: subscription.status,
          hasAccess: subscription.hasAccess,
          accessReason: subscription.accessReason,
          
          // Free tier info
          remainingTrials: subscription.remainingTrials || 0,
          totalTrials: subscription.maxFreeTrials || 0,
          usedTrials: subscription.trialUsageCount || 0,
          
          // Premium info
          subscriptionEnd: subscription.subscriptionEnd,
          paymentInfo: subscription.paymentInfo,
          lastPaymentCheck: subscription.lastPaymentCheck,
          
          // Status flags
          needsUpgrade: subscription.accessReason === 'FREE_TRIAL_EXHAUSTED',
          needsPayment: subscription.accessReason === 'PREMIUM_PAYMENT_EXPIRED',
          isActive: subscription.status === 'active'
        };
      }
      
      return null;
    } catch (error) {
      return null;
    }
  }
};